import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { isSafeRedirect } from "@/lib/billing";
import { isSsoLinkUrl, rememberSsoProvider, visibleSsoProviders } from "@/lib/sso";
import { useOrgId } from "@/lib/org";
import {
  adjustUnread,
  applyPreferencePatch,
  markAllLoadedRead,
  nextPageParam,
  NOTIFICATIONS_PER_PAGE,
  setReadState,
  toNotification,
  type NotificationPages,
  type NotificationTab,
} from "@/lib/notifications";
import type {
  AnalyticsSeries,
  ApiKey,
  Autopost,
  Billing,
  BillingInterval,
  BillingPlans,
  Comment,
  Customer,
  FeedItem,
  Integration,
  Media,
  Member,
  Notification,
  NotificationPreference,
  NotificationPreferencePatch,
  Organization,
  Page,
  Paged,
  Plug,
  PostError,
  PostGroup,
  PostSet,
  PostSummary,
  ProviderInfo,
  PublicPreview,
  Role,
  SavePostsRequest,
  Signature,
  SsoProvider,
  SsoProviderId,
  Tag,
  UpdateGroupRequest,
  User,
  UserIdentities,
  Webhook,
} from "@/lib/types";

// ---- keys ------------------------------------------------------------------

export const keys = {
  me: ["me"] as const,
  identities: ["me", "identities"] as const,
  ssoProviders: ["sso-providers"] as const,
  providers: ["providers"] as const,
  orgs: ["orgs"] as const,
  org: (o: string) => ["org", o] as const,
  members: (o: string) => ["org", o, "members"] as const,
  apiKeys: (o: string) => ["org", o, "api-keys"] as const,
  customers: (o: string) => ["org", o, "customers"] as const,
  integrations: (o: string) => ["org", o, "integrations"] as const,
  plugs: (o: string, id: string) => ["org", o, "integrations", id, "plugs"] as const,
  posts: (o: string) => ["org", o, "posts"] as const,
  group: (o: string, g: string) => ["org", o, "posts", "group", g] as const,
  media: (o: string) => ["org", o, "media"] as const,
  tags: (o: string) => ["org", o, "tags"] as const,
  signatures: (o: string) => ["org", o, "signatures"] as const,
  sets: (o: string) => ["org", o, "sets"] as const,
  webhooks: (o: string) => ["org", o, "webhooks"] as const,
  autoposts: (o: string) => ["org", o, "autoposts"] as const,
  notifications: (o: string) => ["org", o, "notifications"] as const,
  billingPlans: ["billing", "plans"] as const,
  billing: (o: string) => ["org", o, "billing"] as const,
};

interface MutOptions {
  /** refreshed after success without delaying the mutation (e.g. plan usage) */
  background?: QueryKey[];
  meta?: { planLimitInline?: boolean };
}

/** Mutation that invalidates the given keys on success. */
function useMut<TVars, TResult>(
  fn: (vars: TVars) => Promise<TResult>,
  invalidate: QueryKey[],
  opts: MutOptions = {},
) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: fn,
    meta: opts.meta,
    onSuccess: async () => {
      opts.background?.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
      await Promise.all(invalidate.map((k) => qc.invalidateQueries({ queryKey: k })));
    },
  });
}

// ---- account -----------------------------------------------------------------

export const useProviders = () =>
  useQuery({
    queryKey: keys.providers,
    queryFn: () => api.get<ProviderInfo[]>("providers"),
    staleTime: 5 * 60_000,
  });

/**
 * The signed-in user as the server sees it now (GET /me). Used for
 * `is_platform_admin`, which the server re-checks on every admin request anyway.
 */
export const useMe = (opts: { enabled?: boolean; fresh?: boolean } = {}) =>
  useQuery({
    queryKey: keys.me,
    queryFn: () => api.get<User>("me"),
    enabled: opts.enabled ?? true,
    staleTime: opts.fresh ? 0 : 5 * 60_000,
    refetchOnMount: opts.fresh ? "always" : true,
  });

export const useOrganizations = (enabled = true) =>
  useQuery({ queryKey: keys.orgs, queryFn: () => api.get<Organization[]>("orgs"), enabled });

export function useUpdateProfile() {
  return useMut(
    (
      body: Partial<
        Pick<User, "name" | "avatar_url" | "timezone" | "send_success_emails" | "send_failure_emails">
      >,
    ) => api.patch<User>("me", body),
    [keys.me],
  );
}

/** Changes the password, or sets the first one (no current_password) for SSO-only accounts. */
export const useChangePassword = () =>
  useMut(
    (body: { current_password?: string; new_password: string }) => api.post("me/password", body),
    [keys.identities],
  );

// ---- social login (backend/docs/sso.md) ----------------------------------------

/** Enabled sign-in providers. Public; failures just hide the buttons. */
export const useSsoProviders = () =>
  useQuery({
    queryKey: keys.ssoProviders,
    queryFn: async () =>
      visibleSsoProviders(
        (await api.get<{ providers?: SsoProvider[] }>("auth/sso/providers", { auth: false }))?.providers,
      ),
    staleTime: 30 * 60_000,
  });

export const useIdentities = () =>
  useQuery({ queryKey: keys.identities, queryFn: () => api.get<UserIdentities>("me/identities") });

/**
 * Starts linking a provider: the API returns a one-time start URL (the access
 * token can't ride along on a plain navigation), then the browser leaves for
 * the provider and comes back to /account?sso_linked=… or ?sso_error=….
 */
export const useLinkIdentity = () =>
  useMutation({
    mutationFn: async (provider: SsoProviderId) => {
      const { url } = await api.post<{ url: string }>(`me/identities/${provider}/link`);
      if (!isSsoLinkUrl(url)) throw new Error("The server returned an invalid sign-in address.");
      rememberSsoProvider(provider);
      window.location.assign(url);
    },
  });

export const useUnlinkIdentity = () =>
  useMut(
    (provider: string) => api.delete(`me/identities/${encodeURIComponent(provider)}`),
    [keys.identities],
  );

export const useCreateOrganization = () =>
  useMut((body: { name: string; timezone: string }) => api.post<Organization>("orgs", body), [keys.orgs]);

export const useAcceptInvite = () =>
  useMut((token: string) => api.post<Organization>("invites/accept", { token }), [keys.orgs]);

// ---- organization ------------------------------------------------------------

export function useOrganization() {
  const o = useOrgId();

  return useQuery({ queryKey: keys.org(o), queryFn: () => api.get<Organization>(`orgs/${o}`) });
}

export function useUpdateOrganization() {
  const o = useOrgId();

  return useMut(
    (body: { name?: string; timezone?: string }) => api.patch<Organization>(`orgs/${o}`, body),
    [keys.org(o), keys.orgs],
  );
}

export function useMembers() {
  const o = useOrgId();

  return useQuery({ queryKey: keys.members(o), queryFn: () => api.get<Member[]>(`orgs/${o}/members`) });
}

export function useInviteMember() {
  const o = useOrgId();

  return useMutation({
    mutationFn: (body: { email: string; role: Role; send_email: boolean }) =>
      api.post<{ link: string; expires_at: string }>(`orgs/${o}/members/invite`, body),
  });
}

export function useUpdateMemberRole() {
  const o = useOrgId();

  return useMut(
    ({ userId, role }: { userId: string; role: Role }) => api.patch(`orgs/${o}/members/${userId}`, { role }),
    [keys.members(o)],
  );
}

export function useRemoveMember() {
  const o = useOrgId();

  return useMut((userId: string) => api.delete(`orgs/${o}/members/${userId}`), [keys.members(o), keys.orgs], {
    background: [keys.billing(o)],
  });
}

export function useApiKeys() {
  const o = useOrgId();

  return useQuery({ queryKey: keys.apiKeys(o), queryFn: () => api.get<ApiKey[]>(`orgs/${o}/api-keys`) });
}

export function useCreateApiKey() {
  const o = useOrgId();

  return useMut(
    (name: string) => api.post<ApiKey & { key: string }>(`orgs/${o}/api-keys`, { name }),
    [keys.apiKeys(o)],
  );
}

export function useRevokeApiKey() {
  const o = useOrgId();

  return useMut((id: string) => api.delete(`orgs/${o}/api-keys/${id}`), [keys.apiKeys(o)]);
}

export function useCustomers() {
  const o = useOrgId();

  return useQuery({ queryKey: keys.customers(o), queryFn: () => api.get<Customer[]>(`orgs/${o}/customers`) });
}

export function useSaveCustomer() {
  const o = useOrgId();

  return useMut(
    ({ id, name }: { id?: string; name: string }) =>
      id
        ? api.patch<Customer>(`orgs/${o}/customers/${id}`, { name })
        : api.post<Customer>(`orgs/${o}/customers`, { name }),
    [keys.customers(o)],
  );
}

export function useDeleteCustomer() {
  const o = useOrgId();

  return useMut(
    (id: string) => api.delete(`orgs/${o}/customers/${id}`),
    [keys.customers(o), keys.integrations(o)],
  );
}

// ---- channels ----------------------------------------------------------------

export function useIntegrations() {
  const o = useOrgId();

  return useQuery({
    queryKey: keys.integrations(o),
    queryFn: () => api.get<Integration[]>(`orgs/${o}/integrations`),
  });
}

export function useConnectUrl() {
  const o = useOrgId();

  return useMutation({
    mutationFn: ({
      provider,
      refreshId,
      instanceUrl,
    }: {
      provider: string;
      refreshId?: string;
      instanceUrl?: string;
    }) =>
      api.get<{ url: string }>(`orgs/${o}/integrations/connect/${provider}`, {
        query: { refresh_id: refreshId, instance_url: instanceUrl, return_to: `/o/${o}/channels` },
      }),
  });
}

export function useConnectCredentials() {
  const o = useOrgId();

  return useMut(
    ({
      provider,
      ...body
    }: {
      provider: string;
      fields: Record<string, string>;
      instance_url?: string;
      refresh_id?: string;
    }) => api.post<Integration>(`orgs/${o}/integrations/connect/${provider}`, body),
    [keys.integrations(o)],
    { background: [keys.billing(o)] },
  );
}

export function usePages(integrationId: string | null) {
  const o = useOrgId();

  return useQuery({
    queryKey: ["org", o, "integrations", integrationId, "pages"],
    queryFn: () => api.get<Page[]>(`orgs/${o}/integrations/${integrationId}/pages`),
    enabled: Boolean(integrationId),
  });
}

export function useSelectPage() {
  const o = useOrgId();

  return useMut(
    ({ id, pageId }: { id: string; pageId: string }) =>
      api.post<Integration>(`orgs/${o}/integrations/${id}/pages`, { page_id: pageId }),
    [keys.integrations(o)],
    { background: [keys.billing(o)] },
  );
}

export function useUpdateIntegration() {
  const o = useOrgId();

  return useMut(
    ({
      id,
      ...body
    }: {
      id: string;
      custom_name?: string;
      disabled?: boolean;
      preferred?: boolean;
      posting_times?: { time: number }[];
    }) => api.patch<Integration>(`orgs/${o}/integrations/${id}`, body),
    [keys.integrations(o)],
  );
}

export function useSetIntegrationCustomer() {
  const o = useOrgId();

  return useMut(
    ({ id, customerId }: { id: string; customerId: string | null }) =>
      api.put(`orgs/${o}/integrations/${id}/customer`, { customer_id: customerId }),
    [keys.integrations(o)],
  );
}

export function useDeleteIntegration() {
  const o = useOrgId();

  return useMut(
    (id: string) => api.delete(`orgs/${o}/integrations/${id}`),
    [keys.integrations(o), keys.posts(o)],
    { background: [keys.billing(o)] },
  );
}

export function usePlugs(integrationId: string) {
  const o = useOrgId();

  return useQuery({
    queryKey: keys.plugs(o, integrationId),
    queryFn: () => api.get<Plug[]>(`orgs/${o}/integrations/${integrationId}/plugs`),
  });
}

export function useSavePlug(integrationId: string) {
  const o = useOrgId();

  return useMut(
    (body: { function: string; data: Record<string, string>; activated: boolean }) =>
      api.put<Plug>(`orgs/${o}/integrations/${integrationId}/plugs`, body),
    [keys.plugs(o, integrationId)],
  );
}

export function useDeletePlug(integrationId: string) {
  const o = useOrgId();

  return useMut((plugId: string) => api.delete(`orgs/${o}/plugs/${plugId}`), [keys.plugs(o, integrationId)]);
}

export function useTool<T>(integrationId: string | undefined, tool: string | undefined) {
  const o = useOrgId();

  return useQuery({
    queryKey: ["org", o, "integrations", integrationId, "tool", tool],
    queryFn: () => api.post<T>(`orgs/${o}/integrations/${integrationId}/tools`, { tool }),
    enabled: Boolean(integrationId && tool),
    staleTime: 5 * 60_000,
  });
}

export function useAnalytics(integrationId: string | undefined, days: number) {
  const o = useOrgId();

  return useQuery({
    queryKey: ["org", o, "analytics", integrationId, days],
    queryFn: () =>
      api.get<AnalyticsSeries[]>(`orgs/${o}/integrations/${integrationId}/analytics`, { query: { days } }),
    enabled: Boolean(integrationId),
  });
}

export function useNextSlot() {
  const o = useOrgId();

  return useMutation({
    mutationFn: (integrationId: string) =>
      api.get<{ date: string }>(`orgs/${o}/integrations/${integrationId}/next-slot`),
  });
}

// ---- posts -------------------------------------------------------------------

export function useCalendarPosts(
  range: { start: Date; end: Date },
  filters: { customerId?: string; integrationId?: string },
) {
  const o = useOrgId();

  return useQuery({
    queryKey: [...keys.posts(o), "range", range.start.toISOString(), range.end.toISOString(), filters],
    queryFn: () =>
      api.get<PostSummary[]>(`orgs/${o}/posts`, {
        query: {
          start_date: range.start.toISOString(),
          end_date: range.end.toISOString(),
          customer_id: filters.customerId,
          integration_id: filters.integrationId,
        },
      }),
    placeholderData: keepPreviousData,
    // keep publishing states fresh
    refetchInterval: (q) =>
      q.state.data?.some((p) => p.state === "publishing" || p.state === "queue") ? 10_000 : false,
  });
}

export function usePostsList(page: number, state?: string, integrationId?: string) {
  const o = useOrgId();

  return useQuery({
    queryKey: [...keys.posts(o), "list", page, state, integrationId],
    queryFn: () =>
      api.paged<PostSummary>(`orgs/${o}/posts/list`, {
        query: { page, per_page: 25, state, integration_id: integrationId },
      }),
    placeholderData: keepPreviousData,
    refetchInterval: (q) =>
      q.state.data?.items.some((p) => p.state === "publishing" || p.state === "queue") ? 5_000 : false,
  });
}

export function usePostGroup(groupId: string | null) {
  const o = useOrgId();

  return useQuery({
    queryKey: keys.group(o, groupId ?? ""),
    queryFn: () => api.get<PostGroup>(`orgs/${o}/posts/groups/${groupId}`),
    enabled: Boolean(groupId),
    refetchInterval: (q) => (q.state.data?.state === "publishing" ? 3_000 : false),
  });
}

export function usePostErrors(groupId: string, enabled: boolean) {
  const o = useOrgId();

  return useQuery({
    queryKey: [...keys.group(o, groupId), "errors"],
    queryFn: () => api.get<PostError[]>(`orgs/${o}/posts/groups/${groupId}/errors`),
    enabled,
  });
}

export function useCreatePosts() {
  const o = useOrgId();

  // the composer shows plan limits next to its validation messages
  return useMut((body: SavePostsRequest) => api.post<PostGroup[]>(`orgs/${o}/posts`, body), [keys.posts(o)], {
    background: [keys.billing(o)],
    meta: { planLimitInline: true },
  });
}

export function useUpdatePostGroup() {
  const o = useOrgId();

  return useMut(
    ({ groupId, body }: { groupId: string; body: UpdateGroupRequest }) =>
      api.put<PostGroup>(`orgs/${o}/posts/groups/${groupId}`, body),
    [keys.posts(o)],
    { background: [keys.billing(o)], meta: { planLimitInline: true } },
  );
}

export function useDeletePostGroup() {
  const o = useOrgId();

  return useMut((groupId: string) => api.delete(`orgs/${o}/posts/groups/${groupId}`), [keys.posts(o)], {
    background: [keys.billing(o)],
  });
}

export function useReschedulePost() {
  const o = useOrgId();

  return useMut(
    ({ groupId, date }: { groupId: string; date: string }) =>
      api.patch(`orgs/${o}/posts/groups/${groupId}/date`, { date }),
    [keys.posts(o)],
  );
}

export function useChangePostStatus() {
  const o = useOrgId();

  return useMut(
    ({ groupId, status }: { groupId: string; status: "draft" | "schedule" }) =>
      api.patch(`orgs/${o}/posts/groups/${groupId}/status`, { status }),
    [keys.posts(o)],
  );
}

export function useRetryPost() {
  const o = useOrgId();

  return useMut((groupId: string) => api.post(`orgs/${o}/posts/groups/${groupId}/retry`), [keys.posts(o)]);
}

export function useComments(groupId: string) {
  const o = useOrgId();

  return useQuery({
    queryKey: [...keys.group(o, groupId), "comments"],
    queryFn: () => api.get<Comment[]>(`orgs/${o}/posts/groups/${groupId}/comments`),
  });
}

export function useAddComment(groupId: string) {
  const o = useOrgId();

  return useMut(
    (body: { content: string; parent_id?: string }) =>
      api.post<Comment>(`orgs/${o}/posts/groups/${groupId}/comments`, body),
    [[...keys.group(o, groupId), "comments"]],
  );
}

export function useResolveComment(groupId: string) {
  const o = useOrgId();

  return useMut(
    ({ id, resolved }: { id: string; resolved: boolean }) =>
      api.patch(`orgs/${o}/comments/${id}/resolve`, { resolved }),
    [[...keys.group(o, groupId), "comments"]],
  );
}

export function useDeleteComment(groupId: string) {
  const o = useOrgId();

  return useMut(
    (id: string) => api.delete(`orgs/${o}/comments/${id}`),
    [[...keys.group(o, groupId), "comments"]],
  );
}

// ---- media -------------------------------------------------------------------

export function useMediaLibrary(page: number, mediaType?: string) {
  const o = useOrgId();

  return useQuery({
    queryKey: [...keys.media(o), page, mediaType],
    queryFn: () =>
      api.paged<Media>(`orgs/${o}/media`, { query: { page, per_page: 24, media_type: mediaType } }),
    placeholderData: keepPreviousData,
  });
}

export function useUploadMedia() {
  const o = useOrgId();

  return useMut(
    (file: File) => {
      const form = new FormData();
      form.append("file", file);

      return api.upload<Media>(`orgs/${o}/media`, form);
    },
    [keys.media(o)],
  );
}

export function useImportMedia() {
  const o = useOrgId();

  return useMut((url: string) => api.post<Media>(`orgs/${o}/media/import`, { url }), [keys.media(o)]);
}

export function useUpdateMedia() {
  const o = useOrgId();

  return useMut(
    ({ id, alt }: { id: string; alt: string }) => api.patch<Media>(`orgs/${o}/media/${id}`, { alt }),
    [keys.media(o)],
  );
}

export function useDeleteMedia() {
  const o = useOrgId();

  return useMut((id: string) => api.delete(`orgs/${o}/media/${id}`), [keys.media(o)]);
}

// ---- content helpers -----------------------------------------------------------

function crud<T, Req>(path: string, key: (o: string) => QueryKey) {
  return {
    useList() {
      const o = useOrgId();

      return useQuery({ queryKey: key(o), queryFn: () => api.get<T[]>(`orgs/${o}/${path}`) });
    },
    useSave() {
      const o = useOrgId();

      return useMut(
        ({ id, ...body }: Req & { id?: string }) =>
          id ? api.put<T>(`orgs/${o}/${path}/${id}`, body) : api.post<T>(`orgs/${o}/${path}`, body),
        [key(o)],
      );
    },
    useDelete() {
      const o = useOrgId();

      return useMut((id: string) => api.delete(`orgs/${o}/${path}/${id}`), [key(o)]);
    },
  };
}

export const tagsApi = crud<Tag, { name: string; color: string }>("tags", keys.tags);
export const signaturesApi = crud<Signature, { name: string; content: string; auto_add: boolean }>(
  "signatures",
  keys.signatures,
);
export const setsApi = crud<PostSet, { name: string; content: unknown }>("sets", keys.sets);

// ---- automation ----------------------------------------------------------------

export interface WebhookInput {
  name: string;
  url: string;
  integration_ids: string[];
  active?: boolean;
}

export const webhooksApi = crud<Webhook & { secret?: string }, WebhookInput>("webhooks", keys.webhooks);

export function useTestWebhook() {
  const o = useOrgId();

  return useMut((id: string) => api.post<Webhook>(`orgs/${o}/webhooks/${id}/test`), [keys.webhooks(o)]);
}

export interface AutopostInput {
  title: string;
  feed_url: string;
  content_template: string;
  integration_ids: string[];
  on_slot: boolean;
  add_picture: boolean;
  active: boolean;
}

export const autopostsApi = crud<Autopost, AutopostInput>("autoposts", keys.autoposts);

export function usePreviewFeed() {
  const o = useOrgId();

  return useMutation({
    mutationFn: (body: AutopostInput) => api.post<FeedItem[]>(`orgs/${o}/autoposts/preview`, body),
  });
}

// ---- notifications (backend/docs/notifications.md) ----------------------------------

export const notificationKeys = {
  unread: (o: string) => [...keys.notifications(o), "unread"] as const,
  lists: (o: string) => [...keys.notifications(o), "list"] as const,
  list: (o: string, tab: NotificationTab) => [...keys.notifications(o), "list", tab] as const,
};

/** The bell's list, newest first, a page at a time ("Load more"). */
export function useNotificationList(tab: NotificationTab, enabled: boolean) {
  const o = useOrgId();

  return useInfiniteQuery<
    Paged<Notification>,
    Error,
    NotificationPages,
    ReturnType<typeof notificationKeys.list>,
    number
  >({
    queryKey: notificationKeys.list(o, tab),
    queryFn: async ({ pageParam, signal }) => {
      const res = await api.paged<unknown>(`orgs/${o}/notifications`, {
        query: { page: pageParam, per_page: NOTIFICATIONS_PER_PAGE, unread: tab === "unread" || undefined },
        signal,
      });

      return {
        items: res.items.map(toNotification).filter((n): n is Notification => n !== null),
        meta: res.meta,
      };
    },
    initialPageParam: 1,
    getNextPageParam: (last, pages) => nextPageParam(last, pages),
    enabled,
  });
}

/**
 * The unread count. The live stream keeps it current; while the stream is down
 * it is polled every 120 s instead.
 */
export function useUnreadCount(opts: { poll: boolean }) {
  const o = useOrgId();

  return useQuery({
    queryKey: notificationKeys.unread(o),
    queryFn: () => api.get<{ unread: number }>(`orgs/${o}/notifications/unread`),
    refetchInterval: opts.poll ? 120_000 : false,
  });
}

/** Marks one notification read or unread, optimistically (rolled back on error). */
export function useSetNotificationRead() {
  const o = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (v: { notification: Notification; read: boolean }) =>
      v.read
        ? api.post(`orgs/${o}/notifications/${encodeURIComponent(v.notification.id)}/read`)
        : api.delete(`orgs/${o}/notifications/${encodeURIComponent(v.notification.id)}/read`),
    onMutate: async ({ notification: n, read }) => {
      await qc.cancelQueries({ queryKey: notificationKeys.lists(o) });
      const wasUnread = !n.read_at;
      const readAt = read ? new Date().toISOString() : null;
      qc.setQueriesData<NotificationPages>({ queryKey: notificationKeys.lists(o) }, (d) =>
        setReadState(d, n.id, readAt),
      );
      qc.setQueryData<{ unread: number }>(notificationKeys.unread(o), (d) =>
        d ? { unread: adjustUnread(d.unread, wasUnread, !read) } : d,
      );

      return { wasUnread };
    },
    onError: (_err, { notification: n, read }, ctx) => {
      qc.setQueriesData<NotificationPages>({ queryKey: notificationKeys.lists(o) }, (d) =>
        setReadState(d, n.id, n.read_at),
      );
      if (ctx)
        qc.setQueryData<{ unread: number }>(notificationKeys.unread(o), (d) =>
          d ? { unread: adjustUnread(d.unread, !read, ctx.wasUnread) } : d,
        );
    },
    // the count is cheap; the list keeps its optimistic state
    onSettled: () => qc.invalidateQueries({ queryKey: notificationKeys.unread(o) }),
  });
}

/** Marks everything read (the server bounds it to the newest 1000). */
export function useMarkAllNotificationsRead() {
  const o = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () => api.post(`orgs/${o}/notifications/read-all`),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: keys.notifications(o) });
      const lists = qc.getQueriesData<NotificationPages>({ queryKey: notificationKeys.lists(o) });
      const unread = qc.getQueryData<{ unread: number }>(notificationKeys.unread(o));
      const now = new Date().toISOString();
      qc.setQueriesData<NotificationPages>({ queryKey: notificationKeys.lists(o) }, (d) =>
        markAllLoadedRead(d, now),
      );
      qc.setQueryData(notificationKeys.unread(o), { unread: 0 });

      return { lists, unread };
    },
    onError: (_err, _v, ctx) => {
      ctx?.lists.forEach(([key, data]) => {
        if (data) qc.setQueryData<NotificationPages>(key, data);
      });
      if (ctx?.unread) qc.setQueryData(notificationKeys.unread(o), ctx.unread);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: keys.notifications(o) }),
  });
}

export const notificationPreferencesKey = ["me", "notification-preferences"] as const;

export const useNotificationPreferences = () =>
  useQuery({
    queryKey: notificationPreferencesKey,
    queryFn: () => api.get<{ kinds: NotificationPreference[] }>("me/notification-preferences"),
  });

/**
 * Saves one switch with a partial PUT. Optimistic: the switch flips at once
 * and only that value is reverted when the request fails, so quick changes to
 * other switches are kept.
 */
export function useUpdateNotificationPreference() {
  const qc = useQueryClient();
  const mutationKey = [...notificationPreferencesKey, "update"];

  return useMutation({
    mutationKey,
    mutationFn: (patch: NotificationPreferencePatch) =>
      api.put<{ kinds: NotificationPreference[] }>("me/notification-preferences", patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: notificationPreferencesKey, exact: true });
      const before = qc.getQueryData<{ kinds: NotificationPreference[] }>(notificationPreferencesKey);
      // the inverse patch: the previous value of every field this update touches
      const inverse: NotificationPreferencePatch = {};
      for (const [kind, p] of Object.entries(patch)) {
        const prev = before?.kinds.find((k) => k.kind === kind);
        if (!prev) continue;
        inverse[kind] = {
          ...(p.in_app !== undefined && { in_app: prev.in_app }),
          ...(p.email !== undefined && { email: prev.email }),
        };
      }
      qc.setQueryData<{ kinds: NotificationPreference[] }>(notificationPreferencesKey, (d) =>
        d ? { kinds: applyPreferencePatch(d.kinds, patch) } : d,
      );

      return { inverse };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx)
        qc.setQueryData<{ kinds: NotificationPreference[] }>(notificationPreferencesKey, (d) =>
          d ? { kinds: applyPreferencePatch(d.kinds, ctx.inverse) } : d,
        );
    },
    onSuccess: (res) => {
      // a later change still in flight would be overwritten by this answer
      if (qc.isMutating({ mutationKey }) <= 1 && res?.kinds) qc.setQueryData(notificationPreferencesKey, res);
    },
    onSettled: () => {
      // the legacy account switches mirror post.published / post.failed
      void qc.invalidateQueries({ queryKey: keys.me, exact: true });
      // the bell hides kinds whose bell preference is off
      void qc.invalidateQueries({ queryKey: ["org"], predicate: (q) => q.queryKey[2] === "notifications" });
    },
  });
}

// ---- public ------------------------------------------------------------------------

export const usePublicPreview = (groupId: string) =>
  useQuery({
    queryKey: ["public", groupId],
    queryFn: () => api.get<PublicPreview>(`public/posts/${groupId}`, { auth: false }),
  });

export function usePublicComment(groupId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: (body: { content: string; display_name: string }) =>
      api.post<Comment>(`public/posts/${groupId}/comments`, body, { auth: false }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["public", groupId] }),
  });
}

export interface PostStats {
  scheduled_next_7_days: number;
  drafts: number;
  published_last_7_days: number;
  failed: number;
}

export function usePostStats() {
  const o = useOrgId();

  return useQuery({
    queryKey: [...keys.posts(o), "stats"],
    queryFn: () => api.get<PostStats>(`orgs/${o}/posts/stats`),
  });
}

// ---- billing -----------------------------------------------------------------------

/** Public: plans with prices read from Stripe (cached by the server for an hour). */
export const useBillingPlans = () =>
  useQuery({
    queryKey: keys.billingPlans,
    queryFn: () => api.get<BillingPlans>("billing/plans", { auth: false }),
    staleTime: 60 * 60_000,
  });

export function useBilling(opts: { refetchInterval?: number | false } = {}) {
  const o = useOrgId();

  return useQuery({
    queryKey: keys.billing(o),
    queryFn: () => api.get<Billing>(`orgs/${o}/billing`),
    staleTime: 60_000,
    refetchInterval: opts.refetchInterval,
  });
}

/** Sends the browser to a URL the billing API returned (Stripe Checkout or the portal). */
function leaveFor(url: string) {
  if (!isSafeRedirect(url)) throw new Error("The billing provider returned an invalid address.");
  window.location.assign(url);
}

/**
 * Starts Stripe Checkout. The server answers 409 when a live subscription
 * already exists; billing is refreshed on any error so the screen catches up.
 */
export function useCheckout() {
  const o = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (body: { plan: string; interval: BillingInterval }) => {
      const { url } = await api.post<{ url: string }>(`orgs/${o}/billing/checkout`, body);
      leaveFor(url);
    },
    onError: () => qc.invalidateQueries({ queryKey: keys.billing(o) }),
  });
}

/** Opens the Stripe Customer Portal (409 when the workspace has no Stripe customer yet). */
export function usePortal() {
  const o = useOrgId();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { url } = await api.post<{ url: string }>(`orgs/${o}/billing/portal`);
      leaveFor(url);
    },
    onError: () => qc.invalidateQueries({ queryKey: keys.billing(o) }),
  });
}
